import { Puck } from "@puckeditor/core";
import "@puckeditor/core/puck.css";
import { useEffect, useState, useRef, useCallback } from "react";
import { config } from "../puck/config";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getPage, createPage, updatePage, publishPage } from "@/service/weexpertService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { ArrowLeft, Save, Globe, Loader2 } from "lucide-react";

export function PageEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreateMode = !id;

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const pageIdRef = useRef(id || null);

  useEffect(() => {
    if (!isCreateMode) {
      getPage(id)
        .then((res) => {
          const page = res.data;
          setTitle(page.title || "");
          setSlug(page.slug || "");
          setStatus(page.status || "DRAFT");
          setInitialData(page.draftContent || {});
          pageIdRef.current = page.id;
        })
        .catch(() => {
          toast({ title: "Error", description: "Failed to load page", variant: "destructive" });
          navigate("/pages");
        })
        .finally(() => setLoading(false));
    } else {
      setInitialData({});
    }
  }, [id]);

  const handleSave = useCallback(async (puckData) => {
    if (!title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return null;
    }

    setSaving(true);
    try {
      if (!pageIdRef.current) {
        const res = await createPage({ title, slug, draftContent: puckData });
        pageIdRef.current = res.data.id;
        toast({ title: "Page created" });
        navigate(`/pages/${res.data.id}`, { replace: true });
      } else {
        await updatePage(pageIdRef.current, { title, slug, draftContent: puckData });
        toast({ title: "Page saved" });
      }
      return pageIdRef.current;
    } catch {
      toast({ title: "Error", description: "Failed to save page", variant: "destructive" });
      return null;
    } finally {
      setSaving(false);
    }
  }, [title, slug, navigate]);

  const handlePublish = useCallback(async (puckData) => {
    setPublishing(true);
    try {
      const savedId = await handleSave(puckData);
      if (!savedId) return;
      await publishPage(savedId);
      setStatus("PUBLISHED");
      toast({ title: "Page published" });
    } catch {
      toast({ title: "Error", description: "Failed to publish page", variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  }, [handleSave]);

  if (loading || initialData === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen pt-4">
      <div className="flex items-center gap-4 px-4 py-2 border-b bg-white shrink-0 z-20">
        <Link to="/pages" className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Page title"
          className="max-w-xs"
        />
        <Input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="slug"
          className="max-w-[200px]"
        />
        <Badge variant={status === "PUBLISHED" ? "default" : "secondary"}>
          {status}
        </Badge>
      </div>
      <div className="flex-1 overflow-hidden">
        <Puck
          key={JSON.stringify(initialData)}
          config={config}
          data={initialData}
          onPublish={() => {}}
          renderHeaderActions={({ state }) => (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={saving || publishing}
                onClick={() => handleSave(state.data)}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
              <Button
                size="sm"
                disabled={saving || publishing}
                onClick={() => handlePublish(state.data)}
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Globe className="w-4 h-4 mr-1" />}
                Publish
              </Button>
            </>
          )}
        />
      </div>
    </div>
  );
}
